import {
  Box,
  Button,
  Input,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";
import { getUrl } from "src/Utils/Common";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import {Dropdown, DropdownMenuItem, DropdownNestedMenuItem } from "src/Components/Partials/NestedMenu/DropDown";
import { ArrowRight} from "@mui/icons-material";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import { theme } from "../../../Theme/index";

const CumulativeProductShareChart = ({
  brandSelection,
  setBrandSelection,
  storeSelection,
  setStoreSelection,
  thresholdValue,
  setThresholdValue,
  minimal = false,
  apiData,
}) => {
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState("");
  const [value, setValue] = useState(
    thresholdValue !== undefined ? thresholdValue : 95
  );
  const [selected, setSelected] = useState("");
  const [selectedVsHier, setSelectedVsHier] = useState("brand"); 
  const [selectedVsHierValue, setSelectedVsHierValue] = useState("All"); 
  const [groupedOptions, setGroupedOptions] = useState([]);
  const [secondGroupedOptions, setSecondGroupedOptions] = useState([]);
  const [chartData, setChartData] = useState({
    categories: [],
    cumulativeShare: [],
  });
  const [thresholdSkus, setThresholdSkus] = useState({
    0.2: { sku: "", count: 0 },
    0.5: { sku: "", count: 0 },
    0.95: { sku: "", count: 0 },
  });
  const [menuStores, setMenuStores] = useState([]);
  const [menuBrands, setMenuBrands] = useState([]);
  const projectId = useSelector((state) => state.userProject.projectId);
  const [brand, setBrand] = useState(
    brandSelection !== undefined ? brandSelection : "AFIA"
  );
  const [store, setStore] = useState(
    storeSelection !== undefined ? storeSelection : ""
  );
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const fetchSkusShare = async () => {
    setLoading(true);
    setError("");

    if (!projectId) {
      setError("Project ID not found.");
      setLoading(false);
      return;
    }

    try {
      let filtersParam;
      let hierarchyValue;
      
      if (minimal) {
        // For minimal mode, use apiData and handle filters differently
        hierarchyValue = apiData;
        if (selectedVsHierValue && selectedVsHierValue !== "All") {
          filtersParam = `{"${selectedVsHier}": "${selectedVsHierValue}"}`;
        } else {
          filtersParam = "{}";
        }
      } else {
        // For regular mode, use selected and standard filters
        hierarchyValue = selected;
        filtersParam =
          selectedVsHierValue === "All"
            ? "{}"
            : `{"${selectedVsHier}": "${selectedVsHierValue}"}`;
      }

      const response = await fetch(
        `${getUrl()}/strategy_creation/cumulative_product_share?project_id=${projectId}&hierarchy_value=${hierarchyValue}&date_filter=years&filters=${filtersParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log(result);
      setChartData({
        categories: result.sku || [],
        cumulativeShare: (result.cumulative_value_share || []).map(
          (val) => val * 100
        ),
      });

      // Handle thresholds for minimal mode
      if (minimal) {
        const thresholds = [0.2, 0.5, 0.95];
        const skus = result.sku || [];
        const cumulativeShares = result.cumulative_value_share || [];

        const thresholdResults = thresholds.reduce((acc, threshold) => {
          for (let i = 0; i < cumulativeShares.length; i++) {
            if (cumulativeShares[i] >= threshold) {
              acc[threshold] = {
                sku: skus[i],
                count: i + 1,
              };
              break;
            }
          }
          return acc;
        }, {});
        setThresholdSkus(thresholdResults);
      }
    } catch (err) {
      console.error("Error fetching SKU Share Value:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Reset functions for the chart
  const resetChartData = () => {
    setChartData({ categories: [], cumulativeShare: [] });
    setThresholdSkus({});
  };

  const resetChartFilters = () => {
    setSelected("SAVOLA GROUP");
    setSelectedVsHier("brand");
    setSelectedVsHierValue("All");
    setValue(95); 
  };

  const resetChartError = () => {
    setError("");
  };

  const resetChartLoading = () => {
    setLoading(false);
  };

  const { handleRefresh, isRefreshing } = useRefreshData(fetchSkusShare, [
    resetChartData,
    resetChartFilters,
    resetChartError,
    resetChartLoading,
  ]);



  const calculateThresholdSkus = (chartData, thresholdValue) => {
    const thresholds = [0.2, 0.5, thresholdValue / 100];
    const skus = chartData.categories || [];
    const cumulativeShares = (chartData.cumulativeShare || []).map(
      (val) => val / 100
    );

    return thresholds.reduce((acc, threshold) => {
      for (let i = 0; i < cumulativeShares.length; i++) {
        if (cumulativeShares[i] >= threshold) {
          acc[threshold] = {
            sku: skus[i],
            count: i + 1,
          };
          break;
        }
      }
      // If threshold not reached, set empty values
      if (!acc[threshold]) {
        acc[threshold] = { sku: "", count: 0 };
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    if (chartData.categories.length > 0) {
      const newThresholdSkus = calculateThresholdSkus(chartData, value);
      setThresholdSkus(newThresholdSkus);
    }
  }, [chartData, value]);

  useEffect(() => {
    if (!store) return;
    async function fetchBrandsForStore() {
      if (!projectId) return;
      try {
        const response = await fetch(`${getUrl()}/market_overview/hierarchy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({
            project_id: projectId,
            filters: { store_name: store },
          }),
        });
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const result = await response.json();
        const hierarchyData = result["0"];
        console.log(hierarchyData);
        const dynamicMenuConfig = {};
        Object.entries(hierarchyData).forEach(([key, values]) => {
          dynamicMenuConfig[key] = values;
        });
        setMenuBrands(dynamicMenuConfig.brand || []);
      } catch (error) {
        console.error("Error fetching brands for store:", error);
      }
    }
    fetchBrandsForStore();
  }, [store, projectId]);

  const handleSliderChange = (event, newValue) => {
    setValue(newValue);
    if (setThresholdValue) {
      setThresholdValue(newValue);
    }
  };

  useEffect(() => {
    if (setBrandSelection) {
      setBrandSelection(brand);
    }
  }, [brand, setBrandSelection]);

  useEffect(() => {
    if (setStoreSelection) {
      setStoreSelection(store);
    }
  }, [store, setStoreSelection]);

  const highlightThresholds = {
    low: 50,
    medium: value,
  };

  const options = useMemo(() => ({
    chart: {
      type: "line",
      height: "100%",
      width: "100%",
      stacked: false,
      toolbar: { 
        show: !isMobile,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: isMobile ? "70%" : "60%",
        borderRadius: 4,
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: isMobile ? -90 : -45,
        rotateAlways: isMobile,
        style: {
          fontSize: isMobile ? "8px" : "9px",
        },
        maxHeight: isMobile ? 60 : undefined,
      },
    },
    yaxis: [
      {
        title: {
          text: "Cumulative Share (%)",
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
        min: 0,
        max: 100,
        labels: {
          formatter: (val) => Math.round(val) + "%",
          style: {
            fontSize: isMobile ? "10px" : "12px",
          },
        },
      },
    ],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [0, 2],
      curve: "smooth",
    },
    annotations: {
      points: [
        {
          x: thresholdSkus["0.2"]?.sku || "",
          y: 20,
          marker: {
            size: isMobile ? 4 : 6,
            fillColor: "#fff",
            strokeColor: "#2698FF",
            radius: 2,
          },
          label: {
            offsetY: 0,
            style: {
              borderColor: "#08306b",
              color: "#fff",
              background: "#08306b",
              fontSize: isMobile ? "9px" : "12px",
            },
            text: "#Skus to reach 20% is " + (thresholdSkus["0.2"]?.count ?? 0),
          },
        },
        {
          x: thresholdSkus["0.5"]?.sku || "",
          y: 50,
          marker: {
            size: isMobile ? 4 : 6,
            fillColor: "#fff",
            strokeColor: "#2698FF",
            radius: 2,
          },
          label: {
            offsetY: 0,
            style: {
              borderColor: "#08306b",
              color: "#fff",
              background: "#08306b",
              fontSize: isMobile ? "9px" : "12px",
            },
            text: "#Skus to reach 50% is " + (thresholdSkus["0.5"]?.count ?? 0),
          },
        },
        {
          x: thresholdSkus[(value / 100).toString()]?.sku || "",
          y: value,
          marker: {
            size: isMobile ? 4 : 6,
            fillColor: "#fff",
            strokeColor: "#2698FF",
            radius: 2,
          },
          label: {
            offsetY: 0,
            style: {
              borderColor: "#08306b",
              color: "#fff",
              background: "#08306b",
              fontSize: isMobile ? "9px" : "12px",
            },
            text:
              "#Skus to reach " +
              value.toString() +
              "% " +
              "is " +
              (thresholdSkus[(value / 100).toString()]?.count ?? 0),
          },
        },
      ],
    },
    legend: {
      position: isMobile ? "bottom" : "top",
      fontSize: isMobile ? "10px" : "12px",
      itemMargin: {
        horizontal: isMobile ? 5 : 10,
        vertical: isMobile ? 5 : 10,
      },
    },
    colors: undefined, // allow per-point fillColor to take effect
  }), [isMobile, chartData.categories, thresholdSkus, value]);

  const series = [
    {
      name: "Cumulative Product Share",
      type: "column",
      data: chartData.cumulativeShare.map((val, idx) => ({
        x: chartData.categories[idx],
        y: val,
        fillColor:
          val >= highlightThresholds.medium
            ? "#CD393B" // red
            : val >= highlightThresholds.low
            ? "#08306B" // orange
            : "#8ACAEC", // blue
      })),
    },
  ];

  useEffect(() => {
    async function fetchHierarchyData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }

      try {
        const response = await fetch(`${getUrl()}/market_overview/hierarchy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({ project_id: projectId }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        const hierarchyData = result["0"]; // Extract the hierarchy response from API

        const filteredHierarchyData = {};
        Object.entries(hierarchyData).forEach(([key, values]) => {
          if (
            !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(key)
          ) {
            filteredHierarchyData[key] = values;
          }
        });

        const groupedOptions = Object.entries(filteredHierarchyData).map(
          ([key, value]) => ({
            label: key,
            children: value,
          })
        );

        const finalGroupedOptions = [
          ...groupedOptions,
          { label: "channel", children: [], disabled: true },
          { label: "retailer", children: [], disabled: true },
        ];

        const secondGroupedOptions = [
          { label: "All", children: [] },
          ...Object.entries(filteredHierarchyData).map(([key, value]) => ({
            label: key,
            children: value,
          })),
          { label: "channel", children: [], disabled: true },
          { label: "retailer", children: [], disabled: true },
        ];

        // secondGroupedOptions.push({label: "All", children:[]});
        setSecondGroupedOptions(secondGroupedOptions);
        setGroupedOptions(finalGroupedOptions);
        if (brandSelection) {
          setSelected(brandSelection);
        } else {
          setSelected("SAVOLA GROUP");
        }
        if (storeSelection) {
          setSelectedVsHierValue(storeSelection);
          Object.entries(hierarchyData).forEach(([key, values]) => {
            if (values.includes(storeSelection)) {
              setSelectedVsHier(key);
            }
          });
        } else {
          setSelectedVsHierValue("All");
        }
        console.log(hierarchyData);
        const dynamicMenuConfig = {}; // Add 'All Market'
        Object.entries(filteredHierarchyData).forEach(([key, values]) => {
          dynamicMenuConfig[key] = values;
        });
        dynamicMenuConfig["channel"] = [];
        dynamicMenuConfig["retailer"] = [];
        setMenuStores(dynamicMenuConfig.store_name || []);
        setMenuBrands(dynamicMenuConfig.brand || []);
        console.log(dynamicMenuConfig);
        setMenuStores(dynamicMenuConfig.store_name); // Update state with API response
      } catch (error) {
        console.error("Error fetching hierarchy data:", error);
      }
    }

    fetchHierarchyData();
  }, [projectId]);

  useEffect(() => {
    async function fetchHierarchyData() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }

      try {
        const response = await fetch(`${getUrl()}/market_overview/hierarchy`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
          body: JSON.stringify({ project_id: projectId }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        const hierarchyData = result["0"];

        const filteredHierarchyData = {};
        Object.entries(hierarchyData).forEach(([key, values]) => {
          if (
            !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(key)
          ) {
            filteredHierarchyData[key] = values;
          }
        });

        const dynamicMenuConfig = {};
        Object.entries(filteredHierarchyData).forEach(([key, values]) => {
          dynamicMenuConfig[key] = values;
        });
        dynamicMenuConfig["channel"] = [];
        dynamicMenuConfig["retailer"] = [];
        setMenuStores(dynamicMenuConfig.store_name);
      } catch (error) {
        console.error("Error fetching hierarchy data:", error);
      }
    }

    fetchHierarchyData();
  }, [projectId]);

  const handleOptionSelect = (hier, hierValue) => {
    console.log(hier, hierValue);
    const vsOption = {};
    vsOption[hier] = hierValue;
    setSelectedVsHier(hier);
    setSelectedVsHierValue(hierValue);
  };

  // Sync state with props if they change (for Canvas restore)
  useEffect(() => {
    if (brandSelection !== undefined && brandSelection !== brand)
      setBrand(brandSelection);
  }, [brandSelection]);
  useEffect(() => {
    if (storeSelection !== undefined && storeSelection !== store)
      setStore(storeSelection);
  }, [storeSelection]);
  useEffect(() => {
    if (thresholdValue !== undefined && thresholdValue !== value)
      setValue(thresholdValue);
  }, [thresholdValue]);

  useEffect(() => {
    if (projectId) {
      if (minimal) {
        if (apiData && selectedVsHier && selectedVsHierValue) {
          fetchSkusShare();
        } 
      } else {
        if (selectedVsHierValue) {
          fetchSkusShare();
        }
      }
    }
  }, [projectId, minimal, apiData, selectedVsHier, selectedVsHierValue, selected]);
  return (
    <Box
      sx={{
        width: {
          xs: "100%",
          sm: "1100px",
        },
        height: {
          xs: "450px",
          sm: "500px",
        },
        margin: "auto",
        overflowX: "hidden",
        overflowY: "hidden",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!minimal && (
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "0.9rem",
              sm: "1.2rem",
              md: "1.3rem",
            },
            px: {
              xs: 0.5,
              sm: 0.75,
              md: 1,
            },
            color: theme.palette.primary.main,
            textAlign: "center",
            m: 0,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: { xs: 32, sm: 48 },
            mb: { xs: 0.5, sm: 0 },
          }}
        >
          Product Assortment
        </Typography>
      )}
      {!minimal ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "4px", sm: "16px" },
            flexDirection: "row",
            width: "100%",
            px: { xs: 1, sm: 0 },
            mt: { xs: -0.5, sm: 0 },
            mb: { xs: 0.5, sm: 0 },
          }}
        >
          <Box
            sx={{ minWidth: { xs: 0, sm: 120 }, flex: { xs: 1, sm: "none" } }}
          >
            <Dropdown
              trigger={
                <Button
                  endIcon={<ArrowDropDownIcon sx={{ color: "#6C63FF" }} />}
                  sx={{
                    fontWeight: 500,
                    padding: "6px 16px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    minHeight: "36px",
                    width: "100%",
                    maxWidth: { xs: "150px", sm: "250px" },
                    textTransform: "none",
                    fontSize: { xs: "12px", sm: "18px" },
                    display: "flex",
                    mx: "auto",
                    alignItems: "center",
                    gap: 1,
                    "& .MuiSelect-icon": { color: "#6C63FF" },
                    boxShadow: "none",
                    "&:hover": {
                      color: "#1976d2",
                      background: "none",
                      boxShadow: "none",
                    },
                  }}
                >
                  {selected || "SAVOLA GROUP"}
                </Button>
              }
              menu={groupedOptions.map((group) =>
                group.children && group.children.length > 0 ? (
                  <DropdownNestedMenuItem
                    key={group.label}
                    label={group.label}
                    rightIcon={<ArrowRight />}
                    menu={group.children.map((child, childIndex) => (
                      <DropdownMenuItem
                        key={`${group.label}-${childIndex}`}
                        onClick={() => {
                          setBrand(child);
                          setSelected(child);
                          setSelectedVsHierValue("");
                          setIsFirstLoad(false);
                        }}
                      >
                        {child}
                      </DropdownMenuItem>
                    ))}
                  />
                ) : (
                  <DropdownMenuItem
                    key={group.label}
                    onClick={() => {
                      setSelected(group.label);
                    }}
                    disabled={group.disabled}
                    sx={
                      group.disabled
                        ? {
                            color: "#bebebe",
                            cursor: "not-allowed",
                            "&:hover": { backgroundColor: "transparent" },
                          }
                        : {}
                    }
                  >
                    {group.label}
                  </DropdownMenuItem>
                )
              )}
            />
          </Box>
          <Typography
            sx={{
              color: "#0a00c2",
              fontSize: { xs: "16px", sm: "20px" },
              fontWeight: 500,
            }}
          >
            |
          </Typography>
          <Box
            sx={{ minWidth: { xs: 0, sm: 120 }, flex: { xs: 1, sm: "none" } }}
          >
            <Dropdown
              trigger={
                <Button
                  endIcon={<ArrowDropDownIcon sx={{ color: "#6C63FF" }} />}
                  sx={{
                    fontWeight: 500,
                    padding: "6px 16px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    minHeight: "36px",
                    width: "100%",
                    maxWidth: { xs: "150px", sm: "250px" },
                    textTransform: "none",
                    fontSize: { xs: "12px", sm: "18px" },
                    display: "flex",
                    mx: "auto",
                    alignItems: "center",
                    gap: 1,
                    "& .MuiSelect-icon": { color: "#6C63FF" },
                    boxShadow: "none",
                    "&:hover": {
                      color: "#1976d2",
                      background: "none",
                      boxShadow: "none",
                    },
                  }}
                >
                  {selectedVsHierValue
                    ? selectedVsHierValue
                    : isFirstLoad
                    ? "All"
                    : "Select an option"}
                </Button>
              }
              menu={secondGroupedOptions.map((group) =>
                group.children && group.children.length > 0 ? (
                  <DropdownNestedMenuItem
                    key={group.label}
                    label={group.label}
                    rightIcon={<ArrowRight />}
                    menu={group.children.map((child, childIndex) => (
                      <DropdownMenuItem
                        key={`${group.label}-${childIndex}`}
                        onClick={() => {
                          setStore(child);
                          handleOptionSelect(group.label, child);
                        }}
                      >
                        {child}
                      </DropdownMenuItem>
                    ))}
                  />
                ) : (
                  <DropdownMenuItem
                    key={group.label}
                    onClick={() => setSelectedVsHierValue(group.label)}
                    disabled={group.disabled}
                    sx={
                      group.disabled
                        ? {
                            color: "#bebebe",
                            cursor: "not-allowed",
                            "&:hover": { backgroundColor: "transparent" },
                          }
                        : {}
                    }
                  >
                    {group.label}
                  </DropdownMenuItem>
                )
              )}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ minWidth: 120 }}>
            <Dropdown
              trigger={
                <Button
                  endIcon={<ArrowDropDownIcon sx={{ color: "#6C63FF" }} />}
                  sx={{
                    fontWeight: 500,
                    padding: "6px 16px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    minHeight: "36px",
                    width: "100%",
                    maxWidth: { xs: "150px", sm: "250px" },
                    textTransform: "none",
                    fontSize: { xs: "14px", sm: "18px" },
                    display: "flex",
                    mx: "auto",
                    alignItems: "center",
                    gap: 1,
                    "& .MuiSelect-icon": { color: "#6C63FF" },
                    boxShadow: "none",
                    "&:hover": {
                      color: "#1976d2",
                      background: "none",
                      boxShadow: "none",
                    },
                  }}
                >
                  {selectedVsHierValue
                    ? selectedVsHierValue
                    : isFirstLoad
                    ? "All"
                    : "Select an option"}
                </Button>
              }
              menu={secondGroupedOptions.map((group) =>
                group.children && group.children.length > 0 ? (
                  <DropdownNestedMenuItem
                    key={group.label}
                    label={group.label}
                    rightIcon={<ArrowRight />}
                    menu={group.children.map((child, childIndex) => (
                      <DropdownMenuItem
                        key={`${group.label}-${childIndex}`}
                        onClick={() => handleOptionSelect(group.label, child)}
                      >
                        {child}
                      </DropdownMenuItem>
                    ))}
                  />
                ) : (
                  <DropdownMenuItem
                    key={group.label}
                    onClick={() => setSelectedVsHierValue(group.label)}
                  >
                    {group.label}
                  </DropdownMenuItem>
                )
              )}
            />
          </Box>
        </>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: "8px", sm: "16px" },
          width: "100%",
          px: { xs: 1, sm: 0 },
          mt: { xs: -0.5, sm: 0 },
          mb: { xs: 0.5, sm: 0 },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: 400 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          <Typography
            align="center"
            sx={{
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Select Threshold
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              width: "100%",
            }}
          >
            <Slider
              size="small"
              value={value}
              aria-label="Small"
              valueLabelDisplay="auto"
              marks
              min={50}
              onChange={handleSliderChange}
              sx={{ 
                flex: 1,
                "& .MuiSlider-valueLabel": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
                "& .MuiSlider-markLabel": {
                  fontSize: { xs: "0.625rem", sm: "0.75rem" },
                },
              }}
            />
            <Input
              value={value}
              size="small"
              disabled
              sx={{
                width: { xs: "40px", sm: "60px" },
                textAlign: "center",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  padding: { xs: "4px", sm: "6.5px 14px" },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
      {loading || isRefreshing ? (
        <FacebookCircularProgress />
      ) : chartData.categories.length === 0 ? (
        <FacebookCircularProgress
          loading={false}
          hasData={false}
          onRefresh={handleRefresh}
          message="No data available - Click to reset chart"
        />
      ) : !selected || !selectedVsHierValue ? (
        <FacebookCircularProgress
          loading={false}
          hasData={false}
          onRefresh={handleRefresh}
          message="Please select filters - Click to reset chart"
        />
      ) : (
        <Chart
          options={options}
          series={series}
          type="line"
          width={isMobile ? "120%" : "100%"}
          height={isMobile ? "55%" : "70%"}
        />
      )}
    </Box>
  );
};

export default CumulativeProductShareChart;
